import { useState, useEffect, useRef } from 'react';
import './AnimatedMockup.css';

function AnimatedMockup({ mousePosition }) {
  const [isHovered, setIsHovered] = useState(false);
  const [localMouse, setLocalMouse] = useState({ x: 0, y: 0 });
  const [typedText, setTypedText] = useState('');
  const [chartData, setChartData] = useState([45, 60, 75, 65, 85]);
  const [salesCount, setSalesCount] = useState(0);
  const [revenueCount, setRevenueCount] = useState(0);

  const fullText = "Summer Festival 2024";
  
  // Typing animation
  useEffect(() => {
    if (!isHovered) {
      setTypedText('');
      return;
    }

    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setTypedText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
      }
    }, 100);

    return () => clearInterval(typingInterval);
  }, [isHovered]);

  // Animated chart bars
  useEffect(() => {
    if (!isHovered) return;

    const chartInterval = setInterval(() => {
      setChartData(prev => prev.map(() => Math.random() * 50 + 40));
    }, 2000);

    return () => clearInterval(chartInterval);
  }, [isHovered]);

  // Counter animations
  useEffect(() => {
    if (!isHovered) {
      setSalesCount(0);
      setRevenueCount(0);
      return;
    }

    const targetSales = 1247;
    const targetRevenue = 24580;
    const duration = 1500;
    const steps = 60;
    const stepDuration = duration / steps;

    let currentStep = 0;

    const counterInterval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      setSalesCount(Math.floor(targetSales * progress));
      setRevenueCount(Math.floor(targetRevenue * progress));

      if (currentStep >= steps) {
        clearInterval(counterInterval);
      }
    }, stepDuration);

    return () => clearInterval(counterInterval);
  }, [isHovered]);

  return (
    <div 
  className="macbook-container"
  onMouseEnter={() => setIsHovered(true)}
  onMouseLeave={() => {
    setIsHovered(false);
    setLocalMouse({ x: 0, y: 0 });
  }}
  onMouseMove={(e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 20;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 20;
    setLocalMouse({ x, y });
  }}
  style={{
    transform: `perspective(2000px) rotateX(${localMouse.y * 0.3}deg) rotateY(${localMouse.x * 0.3}deg)`,
    transition: isHovered ? 'none' : 'transform 0.4s ease-out'
  }}
>
      {/* MacBook Body */}
      <div className="macbook-body">
        {/* Screen */}
        <div className="macbook-screen">
          <div className="screen-bezel">
            <div className="screen-content">
              
              {/* Dashboard Header */}
              <div className="dashboard-header">
                <div className="header-left">
                  <div className="dashboard-logo">Tick-etc.</div>
                  <div className="dashboard-tabs">
                    <div className="tab active">Overview</div>
                    <div className="tab">Events</div>
                    <div className="tab">Sales</div>
                  </div>
                </div>
                <div className="header-right">
                  <div className="notification-dot"></div>
                  <div className="user-avatar">JS</div>
                </div>
              </div>

              {/* Main Dashboard Content */}
              <div className="dashboard-content">
                
                {/* Event Title with Typing Effect */}
                <div className="event-title-section">
                  <div className="event-label">Active Event</div>
                  <h2 className="event-title">
                    {typedText}
                    <span className={`cursor ${isHovered ? 'blink' : ''}`}>|</span>
                  </h2>
                </div>

                {/* Stats Grid */}
                <div className="stats-grid">
                  <div className={`stat-card ${isHovered ? 'animate' : ''}`}>
                    <div className="stat-icon">🎫</div>
                    <div className="stat-info">
                      <div className="stat-label">Tickets Sold</div>
                      <div className="stat-value">
                        {salesCount.toLocaleString()}
                        <span className="stat-total">/2000</span>
                      </div>
                      <div className="stat-trend positive">↑ 23%</div>
                    </div>
                  </div>

                  <div className={`stat-card ${isHovered ? 'animate' : ''}`} style={{ animationDelay: '0.1s' }}>
                    <div className="stat-icon">💰</div>
                    <div className="stat-info">
                      <div className="stat-label">Revenue</div>
                      <div className="stat-value">
                        €{revenueCount.toLocaleString()}
                      </div>
                      <div className="stat-trend positive">↑ 18%</div>
                    </div>
                  </div>

                  <div className={`stat-card ${isHovered ? 'animate' : ''}`} style={{ animationDelay: '0.2s' }}>
                    <div className="stat-icon">👥</div>
                    <div className="stat-info">
                      <div className="stat-label">Live Visitors</div>
                      <div className="stat-value">
                        {isHovered ? '142' : '0'}
                      </div>
                      <div className="live-indicator">
                        <span className="live-dot"></span>
                        Live now
                      </div>
                    </div>
                  </div>
                </div>

                {/* Animated Chart */}
                <div className="chart-section">
                  <div className="chart-header">
                    <h3>Sales This Week</h3>
                    <div className="chart-legend">
                      <span className="legend-item">
                        <span className="legend-dot"></span>
                        Tickets
                      </span>
                    </div>
                  </div>
                  <div className="chart-container">
                    <div className="chart-bars">
                      {chartData.map((value, index) => (
                        <div key={index} className="chart-bar-wrapper">
                          <div 
                            className={`chart-bar ${isHovered ? 'animated' : ''}`}
                            style={{ 
                              height: `${value}%`,
                              animationDelay: `${index * 0.1}s`
                            }}
                          >
                            <div className="bar-value">{Math.floor(value * 5)}</div>
                          </div>
                          <div className="chart-label">
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'][index]}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Recent Transactions Table */}
                <div className="transactions-section">
                  <h3>Recent Transactions</h3>
                  <div className="transactions-table">
                    {[
                      { name: 'John Smith', ticket: 'VIP', amount: '€125', time: '2m ago' },
                      { name: 'Emma Davis', ticket: 'General', amount: '€45', time: '5m ago' },
                      { name: 'Mike Johnson', ticket: 'VIP', amount: '€125', time: '8m ago' },
                    ].map((transaction, index) => (
                      <div 
                        key={index} 
                        className={`transaction-row ${isHovered ? 'slide-in' : ''}`}
                        style={{ animationDelay: `${index * 0.15}s` }}
                      >
                        <div className="transaction-user">
                          <div className="user-icon">{transaction.name[0]}</div>
                          <div className="user-details">
                            <div className="user-name">{transaction.name}</div>
                            <div className="user-ticket">{transaction.ticket}</div>
                          </div>
                        </div>
                        <div className="transaction-amount">{transaction.amount}</div>
                        <div className="transaction-time">{transaction.time}</div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          </div>
          {/* Screen Notch */}
          <div className="screen-notch"></div>
        </div>

        {/* MacBook Base */}
        <div className="macbook-base">
          <div className="base-indent"></div>
        </div>
      </div>

      {/* Shadow */}
      <div className="macbook-shadow"></div>
    </div>
  );
}

export default AnimatedMockup;
